import { useCallback, useEffect, useRef, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getSelection,
  $isRangeSelection,
  $createRangeSelection,
  $isElementNode,
  $getRoot,
  $setSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
  RangeSelection,
  LexicalNode,
} from 'lexical'
import { MessageSquare, Scissors, Feather, Sparkles, UnfoldVertical, ChevronRight, FileSearch } from 'lucide-react'
import { ADD_COMMENT_COMMAND } from './CommentPlugin'
import { CREATE_SUGGESTION_COMMAND } from './SuggestionPlugin'
import { $isSuggestionNode } from '../nodes/SuggestionNode'
import type { CommentData } from '../nodes/CommentNode'
import CommentInputPopup from '../components/CommentInputPopup'
import ProcessingIndicator from '../components/ProcessingIndicator'
import { ai } from '@services/ai/ai-service'
import { toast } from 'react-hot-toast'
import { TextAction } from '@interfaces/ai'
import { usePermanentStore } from '@/contexts/permanentStore'
import { useParams } from 'react-router-dom'
import { useTemporaryStore } from '@/contexts/temporaryStore'
import { $generateHtmlFromNodes } from '@lexical/html'
import { cn } from '@/lib/utils'
import { eventsService } from '@/services/analytics/events-service'
import { EventCategory, EventName } from '@interfaces/events'
import { CustomParagraphNode } from '../review/CustomParagraphNode'

interface FloatingToolbarPluginProps {
  onRequestComment?: (selection: RangeSelection) => void
  currentUser?: string
  showFindRisksBtn?: boolean
}

export default function FloatingToolbarPlugin({
  currentUser = 'Anonymous',
  showFindRisksBtn = false,
}: FloatingToolbarPluginProps) {
  const [editor] = useLexicalComposerContext()
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [selection, setSelection] = useState<RangeSelection | null>(null)
  const [showCommentInput, setShowCommentInput] = useState(false)
  const [commentInputPosition, setCommentInputPosition] = useState({ x: 0, y: 0 })
  const toolbarRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [showToneMenu, setShowToneMenu] = useState(false)
  const { id: publicId } = useParams()
  const isAutoSelectingRef = useRef(false)

  const { setAutoFeedbackModalTimerStart } = usePermanentStore(s => ({
    setAutoFeedbackModalTimerStart: s.setAutoFeedbackModalTimerStart,
  }))

  const { hideAiTooltip, setRiskResultsFilters, addActiveProcessingBlock, removeActiveProcessingBlock } =
    useTemporaryStore(s => ({
      hideAiTooltip: s.hideAiTooltip,
      setRiskResultsFilters: s.setRiskResultsFilters,
      addActiveProcessingBlock: s.addActiveProcessingBlock,
      removeActiveProcessingBlock: s.removeActiveProcessingBlock,
    }))

  const updateToolbar = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    // Skip n revert if we're currently auto-selecting
    if (isAutoSelectingRef.current) {
      isAutoSelectingRef.current = false
      return
    }

    const currentSelection = $getSelection()

    if ($isRangeSelection(currentSelection) && !currentSelection.isCollapsed()) {
      // Hide toolbar immediately during selection
      setIsVisible(false)

      // Delay showing the toolbar to ensure selection is complete
      timeoutRef.current = setTimeout(() => {
        const nativeSelection = window.getSelection()
        const rootElement = editor.getRootElement()

        if (nativeSelection && nativeSelection.rangeCount > 0 && rootElement) {
          const range = nativeSelection.getRangeAt(0)
          const rect = range.getBoundingClientRect()

          // Position toolbar using viewport coordinates
          const toolbarWidth = 224 // w-56 = 224px
          const toolbarHeight = 200 // XXX: approximate height of the toolbar

          // Start with viewport coordinates (relative to the window)
          let viewportX = rect.left
          let viewportY = rect.bottom

          // Check if toolbar would extend beyond the bottom edge
          if (viewportY + toolbarHeight + 10 > window.innerHeight - 20) {
            // Position toolbar above the selection instead
            viewportY = rect.top - toolbarHeight - 10

            // If positioning above would go off the top, position at top with margin
            if (viewportY < 20) {
              viewportY = 20
            }
          }

          // Check if toolbar would extend beyond the right edge
          if (viewportX + toolbarWidth > window.innerWidth - 20) {
            // Position toolbar so its right edge is 20px from viewport right edge
            viewportX = window.innerWidth - toolbarWidth - 20
          }

          // Ensure toolbar doesn't go off the left edge either
          const minViewportX = 20
          if (viewportX < minViewportX) {
            viewportX = minViewportX
          }

          setPosition({ x: viewportX, y: viewportY })
          setSelection(currentSelection)

          if (!hideAiTooltip) {
            setIsVisible(true)
          }
        }
      }, 300) // 300ms delay to ensure selection is complete
    } else {
      setIsVisible(false)
      setSelection(null)
    }
  }, [editor, hideAiTooltip])

  useEffect(() => {
    const unregister = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar()
        return false
      },
      COMMAND_PRIORITY_LOW
    )

    return () => {
      unregister()
      // Cleanup timeout on unmount
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [editor, updateToolbar])

  // Handle clicks outside and scrolling to hide toolbar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target as Node)) {
        const editorElement = editor.getRootElement()
        if (editorElement && !editorElement.contains(event.target as Node)) {
          setIsVisible(false)
        }
      }
    }

    const handleScroll = () => {
      setIsVisible(false)
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('scroll', handleScroll, true)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [isVisible, editor])

  const handleAddComment = () => {
    if (selection) {
      // Show the custom comment input popup
      setCommentInputPosition({ x: position.x, y: position.y + 40 }) // Position below toolbar
      setShowCommentInput(true)
      setIsVisible(false) // Hide the toolbar
    }
  }

  const handleCommentSubmit = (content: string) => {
    if (selection && content.trim()) {
      const commentData: CommentData = {
        id: `comment_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        content: content.trim(),
        author: currentUser,
        timestamp: Date.now(),
        resolved: false,
      }

      editor.dispatchCommand(ADD_COMMENT_COMMAND, commentData)
      setShowCommentInput(false)
      setSelection(null)
    }
  }

  const handleCommentCancel = () => {
    setShowCommentInput(false)
  }

  const handleFindRisks = () => {
    setIsVisible(false)

    editor.getEditorState().read(async () => {
      const currentSelection = $getSelection()
      if (!$isRangeSelection(currentSelection) || currentSelection.isCollapsed()) return

      const anchorNode = currentSelection.anchor.getNode()
      const focusNode = currentSelection.focus.getNode()

      const anchorElement = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow()
      const focusElement = focusNode.getKey() === 'root' ? focusNode : focusNode.getTopLevelElementOrThrow()

      if (anchorElement.getKey() === focusElement.getKey() && $isElementNode(anchorElement)) {
        // __blockId does exists for each node in the editor state
        const blockId = (anchorElement as any).__blockId || (focusElement as any).__blockId
        if (blockId) setRiskResultsFilters({ targetBlock: blockId })

        // fire ai_tool_used event
        eventsService.fireEvent({
          name: EventName.AI_TOOL_USED,
          category: EventCategory.CORE_BEHAVIOR,
          doc_public_id: publicId || '',
          source: 'FIND_RISKS',
          meta: {
            tool_type: 'find_risks',
          },
        })
      }
    })
  }

  const updateSuggestionContent = (suggestionId: string, newContent: string) => {
    editor.update(() => {
      // Find the suggestion node by ID
      const root = $getRoot()
      const children = root.getChildren()

      const suggestionNode = children.find(
        (child: LexicalNode & { __suggestionData?: any }) => child.__suggestionData?.id === suggestionId
      )

      if (suggestionNode && $isSuggestionNode(suggestionNode)) {
        // Update the suggestion data
        const currentData = suggestionNode.getSuggestionData()

        suggestionNode.setSuggestionData({
          ...currentData,

          suggestedHtml: newContent,
        })
      }
    })
  }

  const handleTextAction = async (action: TextAction) => {
    if (!selection) return

    setIsVisible(false)

    // Check if test mode is activated
    const url = new URL(window.location.href)
    const testMode = url.searchParams.get('test') === 'true'
    // --------------------------------------------

    // Get the selected text in a safe way
    editor.update(() => {
      const currentSelection = $getSelection()
      if (!$isRangeSelection(currentSelection) || currentSelection.isCollapsed()) return

      // START: Expand selection to include the entire block if only part is selected -------------
      const anchorNode = currentSelection.anchor.getNode()
      const focusNode = currentSelection.focus.getNode()

      // Use the immediate parent element (block) instead of top-level to handle nested structures better
      const anchorElement = $isElementNode(anchorNode) ? anchorNode : anchorNode.getParentOrThrow()
      const focusElement = $isElementNode(focusNode) ? focusNode : focusNode.getParentOrThrow()

      let targetSelection = currentSelection

      if (anchorElement.getKey() === focusElement.getKey() && $isElementNode(anchorElement)) {
        const expandedSelection = $createRangeSelection()
        expandedSelection.anchor.set(anchorElement.getKey(), 0, 'element')
        expandedSelection.focus.set(anchorElement.getKey(), anchorElement.getChildren().length, 'element')
        targetSelection = expandedSelection

        isAutoSelectingRef.current = true
        $setSelection(targetSelection)
      }
      // END: Expand selection to include the entire block if only part is selected -------------

      const selectedHTML = $generateHtmlFromNodes(editor, targetSelection)

      if (selectedHTML.trim()) {
        let currentSuggestionId: string | null = null

        // Position processing indicator using viewport coordinates
        const blockId =
          (anchorElement as CustomParagraphNode).__blockId || (focusElement as CustomParagraphNode).__blockId
        addActiveProcessingBlock(blockId, 'Working on it...')

        ai.streamingTextAction(
          {
            action: `text_action:${action}`,
            documentPublicId: publicId || '',
            selectedHTML,
            testMode,
          },
          (event: string, data: { fullText?: string; finalText?: string; error?: string }) => {
            if (event === 'error') {
              removeActiveProcessingBlock(blockId)
              currentSuggestionId = null
              toast.error(data.error || 'Failed to process text action')
            } else if (event === 'stream_start') {
              const suggestionId = `suggestion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

              editor.dispatchCommand(CREATE_SUGGESTION_COMMAND, {
                suggestedHtml: '...',
                suggestionId: suggestionId,
              })

              currentSuggestionId = suggestionId
            } else if (event === 'text_chunk') {
              if (currentSuggestionId && data.fullText) {
                updateSuggestionContent(currentSuggestionId, data.fullText)
              }
            } else if (event === 'stream_end') {
              removeActiveProcessingBlock(blockId)

              if (data.finalText) {
                if (currentSuggestionId) updateSuggestionContent(currentSuggestionId, data.finalText)

                toast.success('Done!')
                setAutoFeedbackModalTimerStart(new Date())
              } else {
                toast.error('Looks like we received an empty response from the AI. You might want to try again.')
              }
            }
          },
          (error: any) => {
            removeActiveProcessingBlock(blockId)
            toast.error(error?.response?.data?.error ?? 'Something went wrong while processing your request.')
          }
        )
      }
    })
  }

  return (
    <>
      {isVisible && (
        <div
          ref={toolbarRef}
          className="fixed z-50 w-56 rounded-md border bg-white shadow-md"
          style={{
            left: position.x,
            top: position.y + 10,
            transform: 'none',
          }}
        >
          <div className="py-1">
            <button
              className="flex w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => handleTextAction(TextAction.MAKE_SHORTER)}
            >
              <Scissors className="mr-2 h-4 w-4 text-[#9065B0]" />
              <span>Make shorter</span>
            </button>
            <div className="relative">
              <button
                className="flex w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                onMouseEnter={() => setShowToneMenu(true)}
                onMouseLeave={() => setShowToneMenu(false)}
              >
                <Feather className="mr-2 h-4 w-4 text-[#9065B0]" />
                <span>Change tone</span>
                <ChevronRight className="ml-auto h-4 w-4 text-[#9065B0]" />
              </button>
              {showToneMenu && (
                <div
                  className="absolute left-full top-0 w-48 rounded-md border bg-white shadow-md"
                  onMouseEnter={() => setShowToneMenu(true)}
                  onMouseLeave={() => setShowToneMenu(false)}
                >
                  <button
                    className="flex w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleTextAction(TextAction.TONE_PROFESSIONAL)}
                  >
                    Professional
                  </button>
                  <button
                    className="flex w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleTextAction(TextAction.TONE_CASUAL)}
                  >
                    Casual
                  </button>
                  <button
                    className="flex w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleTextAction(TextAction.TONE_STRAIGHTFORWARD)}
                  >
                    Straightforward
                  </button>
                  <button
                    className="flex w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleTextAction(TextAction.TONE_CONFIDENT)}
                  >
                    Confident
                  </button>
                  <button
                    className="flex w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleTextAction(TextAction.TONE_FRIENDLY)}
                  >
                    Friendly
                  </button>
                </div>
              )}
            </div>
            <button
              className="flex w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => handleTextAction(TextAction.SIMPLIFY_LANGUAGE)}
            >
              <Sparkles className="mr-2 h-4 w-4 text-[#9065B0]" />
              <span>Simplify language</span>
            </button>
            <button
              className="flex w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => handleTextAction(TextAction.MAKE_LONGER)}
            >
              <UnfoldVertical className="mr-2 h-4 w-4 text-[#9065B0]" />
              <span>Make longer</span>
            </button>
            <button
              className="flex w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              onClick={handleAddComment}
            >
              <MessageSquare className="mr-2 h-4 w-4 text-[#9065B0]" />
              <span>Comment</span>
            </button>
            <button
              className={cn(
                'hidden w-full items-center px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100',
                showFindRisksBtn === true && 'flex'
              )}
              onClick={handleFindRisks}
            >
              <FileSearch className="mr-2 h-4 w-4 text-[#9065B0]" />
              <span>Find issues</span>
            </button>
          </div>
        </div>
      )}

      {showCommentInput && (
        <CommentInputPopup
          position={commentInputPosition}
          onSubmit={handleCommentSubmit}
          onCancel={handleCommentCancel}
        />
      )}

      <ProcessingIndicator />
    </>
  )
}
